import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BlogPost } from "../../utils/blogUtils";
import { Button } from "../../components/ui/button";
import { trackEvent } from "../../utils/analytics";

interface BlogPostNavigationProps {
  prevPost: BlogPost | null;
  nextPost: BlogPost | null;
  handlePrevPostClick: () => void;
  handleNextPostClick: () => void;
}

const BlogPostNavigation = ({ 
  prevPost, 
  nextPost, 
  handlePrevPostClick, 
  handleNextPostClick 
}: BlogPostNavigationProps) => {
  return (
    <nav className="mt-12 mb-8 border-t border-gray-200 dark:border-gray-800 pt-8" aria-label="Navegação entre posts">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Previous post */}
        <div className="md:col-span-1">
          {prevPost ? (
            <Link 
              to={`/blog/${prevPost.slug}`}
              className="block p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              onClick={handlePrevPostClick}
              aria-label={`Post anterior: ${prevPost.title}`}
            >
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center mb-2">
                  <ChevronLeft size={16} className="mr-1" />
                  Post Anterior
                </span>
                <h4 className="text-gray-900 dark:text-white font-medium line-clamp-2">{prevPost.title}</h4>
              </div>
            </Link>
          ) : (
            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 opacity-50">
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center mb-2">
                  <ChevronLeft size={16} className="mr-1" />
                  Post Anterior
                </span>
                <h4 className="text-gray-900 dark:text-white font-medium">Não há posts anteriores</h4>
              </div>
            </div>
          )}
        </div>
        
        {/* Back to blog button */}
        <div className="md:col-span-1 flex items-center justify-center">
          <Button 
            variant="outline"
            className="w-full md:w-auto"
            onClick={() => trackEvent('Blog', 'NavigationClick', 'Direction: Back to Blog')}
            asChild
          >
            <Link to="/blog">Voltar para o Blog</Link>
          </Button>
        </div>
        
        {/* Next post */}
        <div className="md:col-span-1">
          {nextPost ? (
            <Link 
              to={`/blog/${nextPost.slug}`}
              className="block p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              onClick={handleNextPostClick}
              aria-label={`Próximo post: ${nextPost.title}`}
            >
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center justify-end mb-2">
                  Próximo Post
                  <ChevronRight size={16} className="ml-1" />
                </span>
                <h4 className="text-gray-900 dark:text-white font-medium text-right line-clamp-2">{nextPost.title}</h4>
              </div>
            </Link>
          ) : (
            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 opacity-50">
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center justify-end mb-2">
                  Próximo Post
                  <ChevronRight size={16} className="ml-1" />
                </span>
                <h4 className="text-gray-900 dark:text-white font-medium text-right">Não há posts mais recentes</h4>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default BlogPostNavigation;
